// ============================================================================
// DineOpsRequisitionRegisterScreen — Restaurant → Reports → Requisition Register (RPT-007, R1)
// ============================================================================
// What departments took from the kitchens at transfer value, by the business
// date it was issued: every issued line, or the totals by department and month
// (the figure a cost-centre charge or a staff-welfare head is made from). Only
// issued requisitions count; the value was fixed at issue. No tax. A tenant-wide
// report: it never reads the header outlet; the Outlet filter narrows it.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, type SyutGridColumn } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { downloadCsv } from 'utils/csvExport';
import type { DineOutletAdmin, RequisitionRegister, RequisitionRegisterRow, RequisitionRegisterSummary } from 'api/types';
import { useRequisitionPurposes } from 'hooks/api/useDineOps';
import { money, purposeOptions } from './requisitionLabels';

const isoOf = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const todayIso = () => isoOf(new Date());
const monthStartIso = () => { const d = new Date(); d.setDate(1); return isoOf(d); };
const formatDate = (iso?: string | null) =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const formatMonth = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
const right = (v: React.ReactNode, strong = false) =>
  <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: strong ? 700 : 400 }}>{v}</span>;

interface Props {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  /** RBAC: {'view','export'}. Fail-closed: [] renders no export. */
  allowedActions?: string[];
}

type Row = RequisitionRegisterRow & { id: string };
type SummaryRow = RequisitionRegisterSummary & { id: string };

const DineOpsRequisitionRegisterScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const [from, setFrom] = useState(monthStartIso());
  const [to, setTo] = useState(todayIso());
  const [outletId, setOutletId] = useState('');
  const [purpose, setPurpose] = useState('');
  // Every purpose, inactive too: the register covers past months.
  const { purposes } = useRequisitionPurposes(true);
  const [view, setView] = useState<'lines' | 'summary'>('summary');
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [outlets, setOutlets] = useState<DineOutletAdmin[]>([]);
  useEffect(() => { dineOpsService.getOutlets(true).then(setOutlets).catch(() => setOutlets([])); }, []);

  const enabled = !!from && !!to && from <= to;
  const [report, setReport] = useState<RequisitionRegister | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!enabled) { setReport(null); return; }
    setLoading(true);
    setError(null);
    try {
      setReport(await dineOpsService.getRequisitionRegister({ from, to, dineOutletId: outletId || undefined, purpose: purpose || undefined }));
    } catch (err: any) {
      setReport(null);
      setError(err?.response?.data?.message || err?.message || 'The report did not load.');
    } finally {
      setLoading(false);
    }
  }, [enabled, from, to, outletId, purpose]);
  useEffect(() => { void load(); }, [load]);

  const q = searchQuery.trim().toLowerCase();
  const rows = useMemo<Row[]>(() => (report?.rows ?? [])
    .filter((r) => !q || `${r.requisitionNumber} ${r.requestingUnitName} ${r.itemName} ${r.outletName} ${r.receivedByName ?? ''}`.toLowerCase().includes(q))
    .map((r, i) => ({ ...r, id: `${r.requisitionNumber}-${i}` })), [report, q]);
  const summary = useMemo<SummaryRow[]>(() => (report?.byDepartmentMonth ?? [])
    .filter((s) => !q || s.requestingUnitName.toLowerCase().includes(q))
    .map((s) => ({ ...s, id: `${s.month}-${s.requestingUnitId}` })), [report, q]);
  const requisitions = useMemo(() => (report?.byDepartmentMonth ?? []).reduce((n, s) => n + s.requisitions, 0), [report]);
  const departments = useMemo(() => new Set((report?.byDepartmentMonth ?? []).map((s) => s.requestingUnitId)).size, [report]);

  const columns = useMemo<SyutGridColumn<Row>[]>(() => [
    { id: 'date', header: 'Issued on', accessorKey: 'issueBusinessDate', size: 110, cell: ({ row }) => formatDate(row.original.issueBusinessDate) },
    { id: 'number', header: 'Requisition', accessorKey: 'requisitionNumber', size: 190, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.requisitionNumber}</span> },
    { id: 'unit', header: 'Department', accessorKey: 'requestingUnitName', size: 170 },
    { id: 'purpose', header: 'Purpose', accessorKey: 'purpose', size: 120, cell: ({ row }) => row.original.purposeName ?? row.original.purpose },
    { id: 'item', header: 'Dish', accessorKey: 'itemName', size: 220 },
    { id: 'qty', header: 'Qty', accessorKey: 'issuedQuantity', size: 70, cell: ({ row }) => right(row.original.issuedQuantity) },
    { id: 'menu', header: 'Menu price', accessorKey: 'menuUnitPrice', size: 100, cell: ({ row }) => right(money(row.original.menuUnitPrice)) },
    { id: 'unitValue', header: 'Unit value', accessorKey: 'transferUnitPrice', size: 100, cell: ({ row }) => right(money(row.original.transferUnitPrice)) },
    { id: 'value', header: 'Value', accessorKey: 'transferAmount', size: 110, cell: ({ row }) => right(money(row.original.transferAmount), true) },
    { id: 'outlet', header: 'Outlet', accessorKey: 'outletName', size: 140 },
    { id: 'received', header: 'Received by', accessorKey: 'receivedByName', size: 140 },
    { id: 'approved', header: 'Approved by', accessorKey: 'approvedByName', size: 140 },
  ], []);

  const summaryColumns = useMemo<SyutGridColumn<SummaryRow>[]>(() => [
    { id: 'month', header: 'Month', accessorKey: 'month', size: 110, cell: ({ row }) => formatMonth(row.original.month) },
    { id: 'unit', header: 'Department', accessorKey: 'requestingUnitName', size: 240 },
    { id: 'count', header: 'Requisitions', accessorKey: 'requisitions', size: 110, cell: ({ row }) => right(row.original.requisitions) },
    { id: 'amount', header: 'Transfer value', accessorKey: 'amount', size: 140, cell: ({ row }) => right(money(row.original.amount), true) },
    { id: 'share', header: 'Share', accessorKey: 'amount', size: 90,
      cell: ({ row }) => right(report && report.total > 0 ? `${((100 * row.original.amount) / report.total).toFixed(1)}%` : '—') },
  ], [report]);

  const exportCsv = useCallback(() => {
    if (!report) { notificationService.toast.warning('Nothing to export yet.'); return; }
    if (view === 'lines')
      downloadCsv(`RequisitionRegister_${from}_${to}.csv`,
        ['Issued on', 'Requisition', 'Outlet', 'Department', 'Purpose', 'Dish', 'Qty', 'Menu price', 'Unit value', 'Value', 'Received by', 'Issued by', 'Approved by'],
        rows.map((r) => [r.issueBusinessDate.slice(0, 10), r.requisitionNumber, r.outletName, r.requestingUnitName, r.purposeName ?? r.purpose,
          r.itemName, r.issuedQuantity, r.menuUnitPrice, r.transferUnitPrice, r.transferAmount, r.receivedByName ?? '', r.issuedByName ?? '',
          r.approvedByName ?? '']));
    else
      downloadCsv(`RequisitionRegister_Summary_${from}_${to}.csv`, ['Month', 'Department', 'Requisitions', 'Transfer value'],
        summary.map((s) => [s.month.slice(0, 7), s.requestingUnitName, s.requisitions, s.amount]));
  }, [report, view, rows, summary, from, to]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [
      { id: 'view', icon: <IconSystem.list size={16} />, label: view === 'lines' ? 'Show by department' : 'Show every line',
        onClick: () => setView((v) => (v === 'lines' ? 'summary' : 'lines')), variant: 'secondary' },
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { void load(); }, variant: 'icon' },
    ];
    if (allowedActions.includes('export'))
      b.push({ id: 'export', icon: <IconSystem.download size={16} />, label: 'Export', tooltip: 'Export to CSV', onClick: exportCsv, variant: 'icon' });
    return b;
  }, [view, load, allowedActions, exportCsv]);

  const outletName = outlets.find((o) => o.id === outletId)?.outletName;
  const headerItems: ReportHeaderItem[] = [
    { label: 'Period', value: `${formatDate(from)} – ${formatDate(to)}`, primary: true, title: 'Business date the requisition was issued' },
    { label: 'Outlet', value: outletName ?? 'Every outlet', icon: 'home', primary: true },
    ...(purpose ? [{ label: 'Purpose', value: purposes.find((p) => p.purposeCode === purpose)?.purposeName ?? purpose }] : []),
    ...(report ? [
      { label: 'Requisitions', value: requisitions },
      { label: 'Departments', value: departments, icon: 'users' as const },
      { label: 'Transfer value', value: money(report.total), icon: 'creditCard' as const, title: 'Fixed at issue; no tax' },
    ] : []),
  ];

  const appliedCount = (from !== monthStartIso() || to !== todayIso() ? 1 : 0) + (outletId ? 1 : 0) + (purpose ? 1 : 0);

  return (
    <EnterpriseScreenShell
      title="Requisition Register"
      description="What departments took from the kitchens at transfer value, by issue date"
      icon={<IconSystem.list size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search department, dish or number' }}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Issued from <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Issued to <span className="text-danger">*</span></label>
                <input type="date" className="form-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Outlet" options={outlets.map((o) => ({ value: o.id, label: `${o.outletName} (${o.outletCode})` }))} value={outletId}
                  onChange={(v) => setOutletId((v as string) || '')} isClearable placeholder="Every outlet" size="sm" menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Purpose" options={purposeOptions(purposes)} value={purpose} onChange={(v) => setPurpose((v as string) || '')}
                  isClearable placeholder="Any purpose" size="sm" menuPortalTarget={document.body} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setFrom(monthStartIso()); setTo(todayIso()); setOutletId(''); setPurpose(''); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Close</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <ReportBody footer={report ? <span>Issued requisitions only, at the transfer value fixed when each was issued: the dish&rsquo;s own transfer price, else the outlet&rsquo;s percentage of its menu price. No tax, no bill.</span> : undefined}>
        {!enabled ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a period in the filters.</div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>{error}</div>
        ) : view === 'lines' ? (
          <SyutGrid data={rows} columns={columns} isLoading={loading} emptyMessage="Nothing was issued to a department in the period." />
        ) : (
          <SyutGrid data={summary} columns={summaryColumns} isLoading={loading} emptyMessage="Nothing was issued to a department in the period." />
        )}
      </ReportBody>
    </EnterpriseScreenShell>
  );
};

export default DineOpsRequisitionRegisterScreen;
